import Modal from "react-modal";
import {ArrowUpRight, Calendar, Clock, Info, Loader, Share, X, Check, AlertCircle} from "react-feather";
import React, {useEffect, useRef, useState} from "react";
import webinarPlaceholder from "../../../images/webinar-placeholder.png";
import userAvatar from "../../../images/svg/broker/user.svg";
import {registerForWebinar} from "../../../services/api/broker";
import WebinarVideo from "./WebinarVideo";
import { moment } from "../../../services/moment-extensions";

const WP_URL = process.env.REACT_APP_WP_BASE_URL;

const WebinarModal = ({onClose, webinar, videoAutoplay, isBrokerSso, changeWebinarRegistrationStatus}) => {

    const [isRegistrationOpen] = React.useState(new Date(webinar.start_datetime) > new Date()
        && webinar.registration_link !== null && !webinar.has_registered);
    const [registrationInProcess, setRegistrationInProgress] = React.useState(false);
    const [registered, setRegistered] = React.useState(webinar.has_registered);
    const [registeredMessage, setRegisteredMessage] = React.useState(webinar.has_registered ? "You have successfully registered for the webinar." : null);
    const [errorMessage, setErrorMessage] = React.useState(null);

    const [openShareMode, setOpenShareModal] = useState(false);
    const shareMenuRef = useRef(null);
    const [shareLinkCopied, setShareLinkCopied] = React.useState(false);

    const formattedDate = moment(webinar.start_datetime).toMediumDate();
    const formatedTimeString = `${moment(webinar.start_datetime).toShortTime()} - ${moment(webinar.end_datetime).toShortTime()} CST`;


    useEffect(() => {
        function handleClickOutside(event) {
            if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
                setOpenShareModal(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [shareMenuRef]);


    const initiateRegisterForWebinar = async () => {
        try {
            setRegistrationInProgress(true);
            await registerForWebinarAutomatically();
            setRegistrationInProgress(false);
        } catch (e) {
            setRegistrationInProgress(false);
            console.error('Error registering for webinar:', e);
        }
    }

    const registerForWebinarAutomatically = async () => {
        try {
            const response = await registerForWebinar(webinar.id);

            if (response.status === 200) {
                setRegistered(true);
                changeWebinarRegistrationStatus(webinar.id, true);
                setRegisteredMessage("You have successfully registered for the webinar.");
            } else {
                setErrorMessage("An unexpected error occurred. Please try again later.");
            }
        } catch (e) {
            console.error('Error registering for webinar:', e);
        }
    }

    const handleShareClick = () => {
        if (isBrokerSso) {
            setOpenShareModal(true);
        } else {
            copyLink("external");
        }
    };

    const copyLink = (type) => {
        let shareURL = `${WP_URL}/mav?id=${webinar.id}`;
        if (type === "internal") {
            shareURL = window.location.href.includes('?id=')
                ? window.location.href
                : `${window.location.href}?id=${webinar.id}`;
        }

        const textField = document.createElement('textarea')
        textField.innerText = shareURL;
        document.body.appendChild(textField)
        textField.select()
        document.execCommand('copy')
        textField.remove()

        setOpenShareModal(false);
        setShareLinkCopied(true);
        setTimeout(() => {
            setShareLinkCopied(false);
        }, 2000);
    };

    return <Modal
        isOpen={true}
        className="sl-modal-content sl-max-modal sl-max-w-1216px-i"
        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">

        <div className="sl-modal-body ">
            <div
                className="sl-modal-header sl-rounded-md sl-flex sl-justify-between sl-bg-gray-50 sl-text-gray-900 sl-px-6 sl-py-16 sl-border-bottom">
                <div
                    className="title sl-font-semibold sl-text-lg sl-mr-auto sl-ml-0 sl-font">
                    {webinar.title}
                </div>
                <div className="sl-cursor-pointer sl-btn-link-gray sl-mr-0">
                    <X onClick={onClose}/>
                </div>
            </div>
            <div className="sl-modal-body"/>
        </div>
        <div className="sl-modal-body">
            <div
                className="sl-modal-body sl-h-fit-content sl-h-auto sl-p-8 sl-max-h-770px sl-overflow-y-auto sl-customized-scrollbar">
                <div className={"sl-flex sl-flex-row sl-gap-10"}>
                    <div className={"sl-flex sl-flex-col sl-gap-5 sl-w-181"}>

                        {!isRegistrationOpen && webinar.video_link ? (
                            <div className="sl-display-flex sl-align-flex-start sl-flex-col">
                                <WebinarVideo videoLink={webinar.video_link} videoAutoplay={videoAutoplay}/>
                            </div>
                        ) : (
                            <img
                                src={webinar.featured_image ? webinar.featured_image.preview : webinarPlaceholder}
                                alt="Grokker"
                                className="sl-object-contain sl-rounded-lg sl-position-center sl-w-724 sl-h-407"
                            />
                        )}

                        <div>
                            <div className={"sl-flex sl-flex-col sl-gap-2"}>
                                <div className={"sl-flex sl-flex-row sl-gap-2 sl-flex-wrap"}>
                                    <span
                                        className={`sl-font sl-text-sm sl-font-medium sl-border-radius-16 sl-p-2-10 sl-text-primary-700 sl-bg-primary-50`}>
                                        Webinar
                                    </span>
                                    {webinar.products.map((product, index) => (
                                        <span key={product.id}
                                              className={`sl-font sl-text-sm sl-font-medium sl-border-radius-16 sl-p-2-10 sl-text-gray-700 sl-bg-gray-100`}>
                                            {product.name}
                                        </span>
                                    ))}
                                </div>

                                <span
                                    className={"sl-text-xxl sl-font sl-font-semibold sl-text-gray-900"}>{webinar.title}</span>
                                <span
                                    className={"sl-text-md sl-text-gray-700 sl-font sl-font-regular"}>{webinar.description}</span>
                            </div>

                            <a href={`${process.env.REACT_APP_APP_BASE_URL}/vendor/${webinar.vendor.slug}/profile`}
                               className={"sl-flex sl-flex-row sl-gap-2 sl-text-primary-700 sl-align-center sl-cursor-pointer sl-pt-16 sl-font sl-text-md sl-font-medium"}>
                                Vendor profile
                                <ArrowUpRight className={"sl-text-primary-700"} size={20}/>
                            </a>
                        </div>

                        {webinar.presenters.length > 0 && <div className={"sl-flex sl-flex-col sl-gap-3 sl-mt-5"}>
                            <span className={"sl-font sl-font-semibold sl-text-xl sl-text-gray-900"}>Presenters:</span>
                            <div className={"sl-flex sl-flex-row sl-justify-space-between sl-gap-10 sl-flex-wrap"}>

                                {webinar.presenters
                                    .filter((presenter) => presenter.name && presenter.name.trim())
                                    .map((presenter, index) => (
                                        <div key={index}
                                             className={"sl-flex sl-flex-row sl-gap-3 sl-align-center sl-w-50 sl-break-all sl-flex-1-1-45"}>

                                            {presenter.photo && presenter.photo.preview ? (
                                                <img src={presenter.photo.preview} alt={presenter.name}
                                                     className={"sl-w-12 sl-h-12 sl-rounded-full"}/>
                                            ) : (
                                                <div
                                                    className={"sl-w-12 sl-h-12 sl-flex sl-items-center sl-justify-center sl-bg-gray-100 sl-rounded-full"}>
                                                    <img src={userAvatar} alt="Presenter"/>
                                                </div>
                                            )}

                                            <div className={"sl-flex-col sl-flex"}>
                                            <span
                                                className={"sl-font sl-text-gray-700 sl-font-semibold sl-text-md"}>{presenter.name || 'Presenter'}</span>
                                                <span
                                                    className={"sl-font sl-text-gray-500 sl-font-regular sl-text-md"}>{presenter.title}</span>
                                            </div>
                                        </div>
                                    ))}

                            </div>
                        </div>}

                    </div>

                    <div className={"sl-flex sl-flex-col sl-gap-8 sl-w-97"}>
                        <div className={"sl-flex sl-flex-col sl-gap-5"}>
                        <span className={"sl-text-xl sl-text-gray-900 sl-font-semibold sl-font"}>
                            {isRegistrationOpen ? 'Register to attend' : 'Webinar Date & Time'}
                        </span>
                            <div className={"sl-flex sl-flex-col sl-gap-4"}>

                                <span
                                    className={"sl-flex sl-align-center sl-flex-row sl-gap-2 sl-text-gray-700 sl-text-md sl-font-medium"}>
                                <Calendar size={20} className={"sl-text-gray-base"}/>{formattedDate}</span>

                                <span
                                    className={"sl-flex sl-align-center sl-flex-row sl-gap-2 sl-text-gray-700 sl-text-md sl-font-medium"}>
                                    <Clock size={20} className={"sl-text-gray-base"}/>{formatedTimeString}</span>
                            </div>
                        </div>

                        {!registered && !errorMessage && isRegistrationOpen &&
                            <div className={"sl-flex sl-flex-col sl-gap-4"}>
                                {!registrationInProcess && <button onClick={() => initiateRegisterForWebinar()}
                                                                   className={"sl-btn sl-btn-primary sl-btn-xl sl-w-fit-content"}>
                                    Register
                                </button>}

                                {registrationInProcess &&
                                    <button className={"sl-btn sl-btn-primary sl-btn-xl sl-w-fit-content sl-flex"}>
                                        <div className="sl-flex sl-align-center sl-gap-2">
                                            <Loader className={`sl-load-animation`} size={20}/> Registering...
                                        </div>
                                    </button>}
                                <span
                                    className={"sl-text-gray-500 sl-text-sm sl-font-medium sl-flex-row sl-flex sl-gap-2"}>
                               <Info size={20} className={"sl-text-gray-400"}/>
                                You will receive an email invitation for the webinar.
                            </span>
                            </div>}

                        {registered && <div
                            className={"sl-flex sl-flex-col sl-gap-4 sl-bg-gray-50 sl-p-3 sl-pr-16 sl-border-radius-8"}>
                            <span className={"sl-text-sm sl-flex-row sl-flex sl-gap-2"}>
                               <Check size={20} className={"sl-text-success-base"}/>
                                <span>
                                    <p className={"sl-text-gray-700 sl-text-sm sl-font-medium"}>{registeredMessage}</p>
                                    <p className={"sl-text-gray-600 sl-text-sm"}>An invitation email has been sent to you.</p>
                                </span>
                            </span>
                        </div>}

                        {errorMessage && <div
                            className={"sl-flex sl-flex-col sl-gap-2 sl-p-4 sl-pr-16 sl-border-radius-8 sl-bg-error-25 sl-border-1 sl-border-error-300"}>
                            <label className={"sl-text-sm sl-flex-row sl-flex sl-gap-2 sl-error"}>
                                <AlertCircle className={`sl-text-error-600`} size={20}/>
                                <span><p
                                    className={"sl-text-gray-700 sl-text-sm sl-font-medium"}>{errorMessage}</p></span>
                            </label>
                        </div>}

                        <div className={"sl-content-divider "}/>

                        <div className={"sl-flex sl-flex-col sl-gap-5"}>
                            <span className={"sl-text-gray-900 sl-font sl-text-xl sl-font-semibold"}>Share</span>
                            <div className={"sl-relative"}>
                                <button
                                    className={"sl-flex sl-flex-row sl-gap-2 sl-btn sl-btn-secondary sl-btn-xl sl-w-fit-content"}
                                    onClick={() => handleShareClick()}
                                >
                                    <Share className={"sl-text-primary-700"} size={20}/>
                                    Share
                                </button>
                                <div ref={shareMenuRef}
                                     className="sl-dropdown-menu sl-left-0 sl-mt-2 sl-shadow-lg sl-max-w-64"
                                     style={{display: openShareMode ? 'block' : 'none'}}>

                                    <div className="sl-dropdown-header">
                                        <label className="sl-avatar-label-group sl-flex sl-mb-0">
                                            <div
                                                className="sl-flex sl-flex-col  sl-pl-1 sl-pt-6px sl-pb-12 sl-overflow-hidden"
                                                onClick={() => copyLink("internal")}>
                                                <span
                                                    className="sl-font sl-font-medium sl-text-sm sl-text-gray-700 sl-cursor-pointer sl-flex">
                                                    Copy link (Share internally)
                                                </span>
                                            </div>
                                        </label>

                                        <label className="sl-avatar-label-group sl-flex sl-mb-0">
                                            <div
                                                className="sl-flex sl-flex-col  sl-pl-1 sl-pt-6px sl-pb-6px sl-overflow-hidden"
                                                onClick={() => copyLink("external")}>
                                                <span
                                                    className="sl-font sl-font-medium sl-text-sm sl-text-gray-700 sl-cursor-pointer sl-flex">
                                                    Copy link (Share with clients)
                                                </span>
                                            </div>
                                        </label>
                                    </div>

                                </div>

                                <p className={"sl-text-gray-500 sl-text-sm sl-font-regular sl-mt-4"}>
                                    {!shareLinkCopied ? "Feel free to share with colleagues and clients!" : "Link successfully copied to clipboard."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
}

export default WebinarModal;
